import {
  parseISO,
  isToday,
  isYesterday,
  format,
  differenceInHours,
  differenceInMinutes,
  parse,
  differenceInCalendarDays,
} from 'date-fns';
import { es } from 'date-fns/locale';
import { resetPostHog } from '@/analytics/posthogClient';
import { clearLoggedInData } from './services/User';
import { CurrencyFormat, getNumberSeparators } from './constants/Currency';
import {
  OrderStatusChoicesType,
  OrderType,
} from './constants/types/models/order/Order';
import {
  orderCarIdentificationStatusChoices,
  orderOemSearchStatusChoices,
  orderProductIdentificationStatusChoices,
  orderStockSearchStatusChoices,
} from './constants/choices/Order';
import { getToken } from './services/_utils';
import { unlinkDeviceFromPush } from './utils/pushLogout';
import { clearReturnUrl, saveReturnUrl } from './utils/returnUrl';
import {
  MessageDocumentMediaType,
  MessageDocumentType,
  MessageDocumentTypeType,
  MessageQuotedType,
  TempMessagesStatus,
} from './constants/types/models/chat/Chat';
import { v4, validate } from 'uuid';
import { SparkleType } from './constants/types/misc/Sparkles';
import {
  CheckConditionsType,
  TimeValuesType,
} from './constants/types/misc/Misc';
import { GlobalFailureStore, GlobalFailureStoreType } from './utils/metadata';
/**
 * Returns an object with formatted date, time, and full string for a given ISO date string.
 * - date: 'Hoy', 'Ayer', or 'dd/MM/yyyy'
 * - time: 'HH:mm'
 * - full: 'Hoy', 'Ayer a las HH:mm', or 'dd/MM/yyyy a las HH:mm'
 */
export const formatDateDetailed = (dateString?: string) => {
  const formattedDate = {
    date: '',
    time: '',
    full: '',
  };
  if (!dateString) {
    return formattedDate;
  }
  let date: Date;
  try {
    date = parseISO(dateString);
    if (isNaN(date.getTime())) throw new Error('Invalid date');
  } catch {
    return formattedDate;
  }
  if (isToday(date)) {
    formattedDate.date = 'Hoy';
    formattedDate.time = format(date, 'HH:mm');
    formattedDate.full = format(date, "'Hoy a las ' HH:mm", { locale: es });
    return formattedDate;
  } else if (isYesterday(date)) {
    formattedDate.date = 'Ayer';
    formattedDate.time = format(date, 'HH:mm');
    formattedDate.full = format(date, "'Ayer a las' HH:mm", { locale: es });
    return formattedDate;
  }
  formattedDate.full = format(date, 'dd-MM-yyyy HH:mm', { locale: es });
  formattedDate.time = format(date, 'HH:mm');
  formattedDate.date = format(date, 'dd/MM/yyyy', { locale: es });
  return formattedDate;
};

/** Returns `true` if the given `value` is a `Number`. Otherwise, returns `false` */
export const isNumber = (value: any) => {
  return (
    !isNaN(value) &&
    value !== true &&
    value !== false &&
    value !== '' &&
    !Array.isArray(value)
  );
};

/** Returns `true` if the given `value` is a `String`. Otherwise, returns `false` */
export const isString = (value: any) => {
  return typeof value === 'string' || value instanceof String;
};

/** Returns a string in CLP format */
export const toCLP = (value?: number | string) => {
  if (isNumber(value)) {
    const formatoCLP = new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
    });
    return `$ ${formatoCLP.format(Number(value)).substring(1)}`;
  }
  return '';
};

/** Returns a __string__ as number from CLP format. E.g. `$1.000` => `1000` */
export const fromCLP = (value?: string) => {
  if (value) {
    return value.replace(/\$\s?|(\.*)/g, '');
  }
  return '';
};

/** Returns the amount prefixed with the tenant's symbol and grouped with the
 * tenant's separators, e.g. `$ 29.226` (es-CL) or `L 29,226` (es-HN).
 * Resolve `currency` with `getCurrencyFormat` / `useQuotationCurrency`. */
export const toCurrency = (
  value: number | string | undefined,
  currency: CurrencyFormat
) => {
  if (!isNumber(value)) return '';
  const formatted = new Intl.NumberFormat(currency.locale, {
    minimumFractionDigits: 0,
  }).format(Number(value));
  return `${currency.symbol} ${formatted}`;
};

/** Returns an array of json `{value: string, label: string}` from a json object,
 * where `value` is obtained from the object's key and `label` from the object's value */
export const choicesToOptions = (choices: { [key: string | number]: any }) => {
  return Object.entries(choices).map(([key, value]) => {
    if (Object.keys(value).includes('label')) {
      return {
        value: key,
        label: value.label as string,
      };
    }
    return {
      value: key,
      label: value as string,
    };
  });
};

export const getElapsedTime = (datetime?: string) => {
  if (!datetime) {
    return null;
  }
  // Get dates
  const now = new Date();
  const parsedDateTime = parseISO(datetime);

  // Get elapsed time
  const minutesPassed = differenceInMinutes(now, parsedDateTime);
  const hoursPassed = differenceInHours(now, parsedDateTime);
  const daysPassed = differenceInCalendarDays(now, parsedDateTime);

  // Get remaining
  const remainingHours = hoursPassed % 24;
  const remainingMinutes = minutesPassed % 60;
  // Get top time
  const top: {
    count?: number;
    time?: 'día' | 'días' | 'hora' | 'horas' | 'minuto' | 'minutos';
    value?: string;
    day?: string;
  } = {};
  let isNow = false;
  if (daysPassed) {
    top.count = daysPassed;
    top.time = daysPassed === 1 ? 'día' : 'días';
  } else if (remainingHours) {
    top.count = remainingHours;
    top.time = remainingHours === 1 ? 'hora' : 'horas';
  } else if (remainingMinutes) {
    top.count = remainingMinutes;
    top.time = remainingMinutes === 1 ? 'minuto' : 'minutos';
  } else {
    isNow = true;
    top.value = `Ahora`;
  }
  if (!isNow) {
    if (top.count! > 0) {
      top.value = `Hace ${top.count} ${top.time}`;
    } else {
      top.value = `Para ${top.count! * -1} ${top.time}`;
    }
  }
  // Return
  return {
    days: daysPassed,
    hours: remainingHours,
    minutes: remainingMinutes,
    top,
  };
};

export const formatDate = (timestamp?: string) => {
  if (!timestamp) return;

  const date = parse(timestamp, 'yyyy-MM-dd', new Date());
  const today = new Date();
  const yesterday = new Date();

  yesterday.setDate(today.getDate() - 1);

  if (date.toDateString() === today.toDateString()) return 'Hoy';
  if (date.toDateString() === yesterday.toDateString()) return 'Ayer';

  const diffDays = Math.floor(
    (today.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
  );

  return new Intl.DateTimeFormat('es-ES', {
    ...(diffDays < 7
      ? {
          weekday: 'long',
        }
      : {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        }),
  }).format(new Date(date.getTime()));
};

export const clearBlankFields = (form: any) => {
  for (const [key, value] of Object.entries(form)) {
    if (/^\s*$/.test(value as any)) {
      delete form[key];
    }
  }
};

export const isEmpty = (value: any) => {
  return (
    value === undefined ||
    value === null ||
    (typeof value === 'string' && value.trim() === '')
  );
};

export const capitalize = (value?: string) => {
  if (value) {
    return value.charAt(0)?.toUpperCase() + value.slice(1);
  }
  return '';
};

export const escapeRegExp = (value: string) => {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

/**
 * Cierra la sesión local y manda al login. Con `{ returnUrl: true }` (sesión vencida o
 * ausente, NO logout manual) guarda el destino actual para volver a él tras autenticarse.
 */
export const goToLoginPage = async (options?: { returnUrl?: boolean }) => {
  // Logout manual (sin returnUrl): desvincula este dispositivo del push ANTES de borrar el
  // token (tope ~1 s). Sesión vencida (returnUrl): nada, el token ya no sirve.
  if (options?.returnUrl !== true) await unlinkDeviceFromPush();
  clearLoggedInData();
  // Unlink this browser from the seller so the next login isn't merged into them.
  resetPostHog();
  // Already on a public auth page: no session to protect, and navigating
  // again here is what causes a reload loop when an unauthenticated call
  // 401s on this same page.
  if (window.location.pathname.includes('/auth/')) {
    return;
  }
  if (options?.returnUrl === true) saveReturnUrl();
  else clearReturnUrl();
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH || '';
  const locale = window.location.pathname.split('/')[1] || 'es';
  window.location.href = `${basePath}/${locale}/auth/login`;
  await new Promise((resolve) => setTimeout(resolve, 5000));
};

export const getOrderStatusValue = (order?: OrderType) => {
  if (!order) {
    return false;
  }
  if (order.status === 'identifying_car' && order.car_identification_status) {
    return orderCarIdentificationStatusChoices[order.car_identification_status];
  } else if (
    order.status === 'identifying_products' &&
    order.product_identification_status
  ) {
    return orderProductIdentificationStatusChoices[
      order.product_identification_status
    ];
  } else if (order.status === 'searching_oems' && order.oem_search_status) {
    return orderOemSearchStatusChoices[order.oem_search_status];
  } else if (
    ['searching_stock', 'quotation_sent'].includes(order.status) &&
    order.stock_search_status
  ) {
    return orderStockSearchStatusChoices[order.stock_search_status];
  }
  return false;
};

export const isOrderNeedingAssistance = (order?: OrderType) => {
  if (!order) {
    return false;
  }
  return (
    (order.status === 'identifying_car' &&
      order.car_identification_status === 'car_unidentified') ||
    (order.status === 'identifying_products' &&
      ['some_products_unidentified', 'products_unidentified'].includes(
        order.product_identification_status || ''
      )) ||
    (order.status === 'searching_oems' &&
      ['some_oems_unidentified', 'oems_unidentified'].includes(
        order.oem_search_status || ''
      )) ||
    ((order.status === 'searching_stock' ||
      order.status === 'quotation_sent') &&
      ['some_products_not_found', 'products_not_found'].includes(
        order.stock_search_status || ''
      ))
  );
};

export type WidgetPath = 'quote/create' | 'chat';

export const getWidgetUrl = (path: WidgetPath = 'quote/create') => {
  const token = getToken();
  const schemaName = token?.['custom:schema_name'];
  const subsidiaryId = token?.['custom:subsidiary_id'];
  const widgetEnvUrl =
    process.env.NEXT_PUBLIC_ENV === 'production' ? '' : '-staging';
  return `https://fasto${widgetEnvUrl}.repartes.com/${schemaName}/${subsidiaryId}/${path}`;
};

export const getWidgetIframeSnippet = (path: WidgetPath = 'quote/create') => {
  if (path === 'chat') {
    return `<iframe
  src="${getWidgetUrl(path)}"
  width="100%"
  height="720"
  allow="microphone"
  style="border: 0;"
></iframe>`;
  }
  return `<iframe
  src="${getWidgetUrl(path)}"
  width="100%"
  height="600"
  style="border: 0;"
></iframe>`;
};

/** Deja solo los dígitos de un teléfono, para poder compararlos entre sí */
export const normalizePhone = (value?: string | null) =>
  (value || '').replace(/\D/g, '');

export const cleanSelectedChatPhone = (chatId: string) => {
  if (chatId) {
    return chatId.split('@')[0].replace('+', '');
  }
  return undefined;
};

export const generateRandomString = (length = 20) => {
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';

  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    result += characters[randomIndex];
  }

  return result;
};

export const formatTimeSince = (
  dateString: string | null | undefined
): string | null => {
  if (!dateString) {
    return null;
  }

  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return null; // Invalid date
  }

  const now = new Date();
  const seconds = Math.round((now.getTime() - date.getTime()) / 1000);

  if (seconds < 0) {
    // Date is in the future, should not happen for 'ai_mark_as_purchase_at'
    // but good to handle. Could return 'en el futuro' or similar if needed.
    return null;
  }

  const minutes = Math.round(seconds / 60);
  const hours = Math.round(minutes / 60);
  const days = Math.round(hours / 24);

  if (days > 0) {
    return `Hace ${days} día${days > 1 ? 's' : ''}`;
  }
  if (hours > 0) {
    return `Hace ${hours} hora${hours > 1 ? 's' : ''}`;
  }
  if (minutes > 0) {
    return `Hace ${minutes} minuto${minutes > 1 ? 's' : ''}`;
  }
  return 'Hace muy poco 🔥';
};

export const truncateString = (
  input: string | undefined,
  maxLength: number
): string => {
  if (!input) return '';
  if (input.length <= maxLength) {
    return input;
  }
  return input.slice(0, maxLength - 3) + '...';
};

const escapeHtml = (text: string) =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

// Escapes the input BEFORE adding markup: chat text comes from end
// customers over WhatsApp, so anything not escaped here is an XSS
// straight into the backoffice session.
export const sanitizeTextToHTML = (text?: string) => {
  if (!text) return '';

  const urlRegex =
    /\b(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,6}(\/[^\\\s]*)?\b/g;
  const newLineRegex = /\n/g;
  const strongRegex = /\*([^*]+)\*/g;

  text = escapeHtml(text);
  text = text.replace(
    urlRegex,
    (match) =>
      `<a href="${match}" target="_blank" rel="noreferrer">${match}</a>`
  );
  text = text.replace(strongRegex, (_, p1) => `<strong>${p1}</strong>`);
  text = text.replace(newLineRegex, '<br />');

  return text;
};

export const isMessageFromClient = (params: {
  chat_id?: string;
  clientPhone?: string | null;
}) => {
  return (
    cleanSelectedChatPhone(params.chat_id || '') ===
    cleanSelectedChatPhone(params.clientPhone || '')
  );
};

export const generateMessageTempKey = () => {
  const chars =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let key = '';
  for (let i = 0; i < 4; i++) {
    key += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return key;
};

export const base64ToFile = (
  base64: string,
  filename: string,
  mimeType: string = 'audio/wav'
) => {
  const fullBase64 = `data:${mimeType};base64,${base64}`;
  const base64Pattern = /^data:(.+);base64,(.*)$/;
  const matches = fullBase64.match(base64Pattern);

  if (!matches || matches.length < 3) {
    throw new Error('Invalid base64 string');
  }

  const mime = matches[1];
  const bstr = atob(matches[2]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], filename, { type: mime });
};

export const fileToBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();

    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
  });

/**
 * Genera una miniatura liviana (data URL) de una imagen redimensionándola con
 * canvas. Evita cargar la imagen full-size en el preview cuando se seleccionan
 * varios archivos a la vez. Si algo falla, cae a la imagen original en base64.
 */
export const createImageThumbnail = async (
  file: File,
  maxSize = 512
): Promise<string> => {
  if (!file.type.startsWith('image/')) return fileToBase64(file);

  const dataUrl = await fileToBase64(file);

  return new Promise<string>((resolve) => {
    const img = new Image();

    img.onload = () => {
      const ratio = Math.min(1, maxSize / Math.max(img.width, img.height));
      const width = Math.max(1, Math.round(img.width * ratio));
      const height = Math.max(1, Math.round(img.height * ratio));

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(dataUrl);
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      try {
        resolve(canvas.toDataURL('image/jpeg', 0.7));
      } catch {
        resolve(dataUrl);
      }
    };

    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
};

/**
 * Ejecuta tareas asíncronas con un límite de concurrencia. Útil para subir
 * varios archivos sin saturar la red ni el hilo principal.
 */
export const runWithConcurrency = async <T>(
  tasks: Array<() => Promise<T>>,
  limit: number
): Promise<void> => {
  let index = 0;

  const workers = Array.from({ length: Math.min(limit, tasks.length) }, () =>
    (async () => {
      while (index < tasks.length) {
        const current = index++;
        await tasks[current]();
      }
    })()
  );

  await Promise.all(workers);
};

export const objectByLabel = <T>(
  items: T[],
  key: keyof T | ((item: T) => string)
) => {
  const getLabel =
    typeof key === 'function' ? key : (item: T) => item[key] as string;
  const orderItems: { [label: string]: T[] } = {};

  items.forEach((item) => {
    const label = getLabel(item);

    if (orderItems[label]) {
      orderItems[label].push(item);
    } else {
      orderItems[label] = [item];
    }
  });

  return orderItems;
};

export const groupBy = <T>(items: T[], groupSize: number) => {
  const result: T[][] = [];

  for (let i = 0; i < items.length; i += groupSize) {
    result.push(items.slice(i, i + groupSize));
  }

  return result;
};

export const generateTempMessage = (
  message?: string,
  clientPhone?: string,
  messageType?: MessageDocumentTypeType,
  quoteMessage?: MessageQuotedType,
  status?: TempMessagesStatus,
  internalMessageId?: string,
  media?: MessageDocumentMediaType
) => {
  if (!internalMessageId) {
    internalMessageId = v4();
  }

  const tempMessage: MessageDocumentType = {
    id: internalMessageId,
    phone: clientPhone,
    device_id: '',
    message_id: internalMessageId,
    websocket_id: internalMessageId,
    type: messageType,
    message,
    chat: {
      id: cleanSelectedChatPhone(clientPhone || ''),
    },
    flow: 'outbound',
    status: status ?? 'stale',
    unread: false,
    tempKey: internalMessageId,
    quoted: quoteMessage,
    reference: 'backoffice',
    date: new Date().toISOString(),
    media,
  };

  return tempMessage;
};

export const isUUID = (value: string) => validate(value);

export const getFileFromUrl = async (
  url: string,
  useProxy = false
): Promise<File> => {
  try {
    const targetUrl = useProxy
      ? `/api/proxy-image?url=${encodeURIComponent(url)}`
      : url;
    const response = await fetch(targetUrl);
    const blob = await response.blob();

    // Get file name
    const urlParts = url.split('/');
    let fileName = urlParts[urlParts.length - 1] || 'downloaded_file';
    fileName = fileName.split('?')[0].split('#')[0];
    if (!fileName.includes('.')) {
      const extension = blob.type.split('/')[1] || 'bin';
      fileName = `file_${Date.now()}.${extension}`;
    }
    // Return
    return new File([blob], fileName, { type: blob.type });
  } catch (error) {
    console.error('Error fetching file:', error);
    if (!useProxy) {
      return getFileFromUrl(url, true);
    }

    throw error;
  }
};

export const convertBlobImageToPng = async (blob: Blob): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const img = document.createElement('img');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);

      canvas.toBlob((newBlob) => {
        if (newBlob) {
          resolve(newBlob);
        } else {
          reject(new Error('Image conversion failed'));
        }
      }, 'image/png');
    };

    img.onerror = reject;
    img.src = URL.createObjectURL(blob);
  });
};

export const ensureS3UrlHasRegion = (url: string, region?: string) => {
  const currentRegion = region || process.env.NEXT_PUBLIC_AWS_SE_REGION;
  const s3GlobalPattern = /^https:\/\/([a-z0-9-]+)\.s3\.amazonaws\.com(\/.+)/;
  const s3RegionalPattern =
    /^https:\/\/([a-z0-9-]+)\.s3\.[a-z0-9-]+\.amazonaws\.com/;
  if (s3RegionalPattern.test(url)) return url;
  const match = url.match(s3GlobalPattern);
  if (!match) return url;
  const bucket = match[1];
  const path = match[2];
  return `https://${bucket}.s3.${currentRegion}.amazonaws.com${path}`;
};

export const getClipboardItemsFromImageUrl = async (imageUrl: string) => {
  const currentImgUrl = ensureS3UrlHasRegion(imageUrl);
  const response = await fetch(currentImgUrl);
  const blob = await response.blob();
  const convertedBlob = await convertBlobImageToPng(blob);
  return [new ClipboardItem({ [convertedBlob.type]: convertedBlob })];
};

export const getFileFromClipboardItems = async (
  clipboardItems: ClipboardItems
) => {
  try {
    // eslint-disable-next-line no-unreachable-loop
    for (const item of clipboardItems) {
      // eslint-disable-next-line no-unreachable-loop
      for (const type of item.types) {
        const blob = await item.getType(type);
        const file = new File([blob], 'clipboard_image.png', {
          type: blob.type,
        });
        return file;
      }
    }
  } catch (error) {
    console.error('Error processing ClipboardItem:', error);
  }
};

export const inputNumberMoneyFormatter = (value?: number) => {
  return `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const inputNumberMoneyParser = (value?: string) => {
  return value?.replace(/\$\s?|(,*)/g, '') as unknown as number;
};

/** `InputNumber` formatter for the tenant's currency. Unlike
 * `inputNumberMoneyFormatter` (hardcoded `$` + comma grouping) it uses the same
 * separators as `toCurrency`, so the price input and the totals shown next to
 * it stop disagreeing (`29.226` vs `29,226`). Only the integer part is grouped;
 * the decimals the user is typing are left alone. */
export const toMoneyInputFormatter =
  (currency: CurrencyFormat) =>
  (value?: string | number): string => {
    const raw = value === undefined || value === null ? '' : String(value);
    if (!raw) return '';
    const { group, decimal } = getNumberSeparators(currency.locale);
    // AntD hands the value over with `.` as the decimal separator.
    const [integer, fraction] = raw.split('.');
    const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, group);
    const body =
      fraction === undefined ? grouped : `${grouped}${decimal}${fraction}`;
    return `${currency.symbol} ${body}`;
  };

/** `InputNumber` parser matching `toMoneyInputFormatter`. Strips the symbol,
 * strips every occurrence of the grouping separator, and normalises the
 * decimal separator back to `.` for `Number()`.
 *
 * The grouping and decimal characters are never the same character for a
 * given locale, so there's no ambiguity to resolve from the string shape —
 * every occurrence of `group` is a thousands mark `toMoneyInputFormatter`
 * inserted, full stop.
 *
 * We used to only strip a `group` char when it sat in a "closed" position
 * (exactly 3 digits behind, nothing after — e.g. `29.226`), to avoid reading
 * a real decimal like `29.5` as `295`. That broke `AntD`'s `InputNumber`,
 * which re-runs the formatter on every keystroke: typing a 5th digit right
 * after a `5.000` group left `5.0000` on the wire — a `group` char now
 * followed by 4 digits — so the old check no longer matched, the dot
 * survived, and `Number("5.0000")` silently saved a 50000 quotation as `5`.
 * Stripping `group` unconditionally removes that failure mode; a fraction is
 * only ever introduced by the locale's actual `decimal` character below. */
export const toMoneyInputParser =
  (currency: CurrencyFormat) =>
  (value?: string): number => {
    if (!value) return value as unknown as number;
    const { group, decimal } = getNumberSeparators(currency.locale);
    const withoutSymbol = value.split(currency.symbol).join('').trim();
    const withoutGroups = group
      ? withoutSymbol.split(group).join('')
      : withoutSymbol;
    return withoutGroups.split(decimal).join('.') as unknown as number;
  };

export const isOrderStatusIn = (
  orderStatus: OrderStatusChoicesType,
  statusToValidate: OrderStatusChoicesType[]
) => {
  return statusToValidate.some((status) => status === orderStatus);
};

export const compareObjects = <T extends { [label: string]: any }>(
  objectA: T,
  objectB: T,
  deepSearch?: boolean
): boolean => {
  const objectAKeys = Object.keys(objectA);
  const objectBKeys = Object.keys(objectB);

  if (objectAKeys.length !== objectBKeys.length) return false;

  if (!deepSearch) {
    return JSON.stringify(objectA) === JSON.stringify(objectB);
  }

  let areEquals = true;

  for (let i = 0; i < objectAKeys.length; i++) {
    if (!areEquals) break;

    const itemA = objectA[objectAKeys[i]];
    const itemB = objectB[objectBKeys[i]];

    if (Array.isArray(itemA) && Array.isArray(itemB)) {
      if (itemA.length !== itemB.length) {
        areEquals = false;
        break;
      }

      areEquals = !itemA.some((item: any, index: number) => {
        if (typeof item === 'object' && typeof itemB[index] === 'object') {
          return compareObjects(item, itemB[index], true);
        }

        return item !== itemB[index];
      });
    }

    if (
      itemA !== null &&
      itemB !== null &&
      typeof itemA === 'object' &&
      typeof itemB === 'object'
    ) {
      areEquals = compareObjects(itemA, itemB, true);
    } else {
      areEquals = itemA === itemB;
    }
  }

  return areEquals;
};

export const random = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min)) + min;

export const generateSparkle = (color = '#FFC700') => {
  return {
    id: String(random(10000, 99999)),
    createdAt: Date.now(),
    // Bright yellow color:
    color,
    size: random(10, 20),
    style: {
      // Pick a random spot in the available space
      top: random(0, 100) + '%',
      left: random(0, 100) + '%',
      // Float sparkles above sibling content
      zIndex: 2,
    },
  } as SparkleType;
};

export const getTimeValueTypeSubset = <T extends readonly TimeValuesType[]>(
  ...values: T
) => values as readonly [...T];

export const getTimeValueFromSeconds = (seconds: number): TimeValuesType => {
  const minutes = 60;
  const hours = 60 * minutes;
  const days = 24 * hours;
  const weeks = 7 * days;

  if (seconds % weeks === 0) return 'weeks';
  if (seconds % days === 0) return 'days';
  if (seconds % hours === 0) return 'hours';
  if (seconds % minutes === 0) return 'minutes';

  return 'seconds';
};

export const timeValueToSeconds = (timeType: TimeValuesType, value: number) => {
  const minutes = 60;
  const hours = 60 * minutes;
  const days = 24 * hours;
  const weeks = 7 * days;

  switch (timeType) {
    case 'seconds':
      return value;
    case 'minutes':
      return value * minutes;
    case 'hours':
      return value * hours;
    case 'days':
      return value * days;
    case 'weeks':
      return value * weeks;
  }
};

export const secondsToTimeValue = (timeType: TimeValuesType, value: number) => {
  const minutes = 60;
  const hours = 60 * minutes;
  const days = 24 * hours;
  const weeks = 7 * days;

  switch (timeType) {
    case 'seconds':
      return value;
    case 'minutes':
      return value / minutes;
    case 'hours':
      return value / hours;
    case 'days':
      return value / days;
    case 'weeks':
      return value / weeks;
  }
};

const checkCondition = <T>(object: T, condition: CheckConditionsType<T>) => {
  let objectFieldValue;
  let deniedObjectFieldValue = false;

  if (typeof condition.field !== 'object') {
    objectFieldValue = object[condition.field as keyof T];
  } else {
    objectFieldValue = object[condition.field.not];
    deniedObjectFieldValue = true;
  }

  const isValueInCondition = 'value' in condition;
  const isValidValue = objectFieldValue === condition.value;

  if (condition.dependencies) {
    const {
      not: falsyValues,
      default: truthlyValues,
      connector,
    } = condition.dependencies;

    let dependenciesNotMet: boolean;
    let dependenciesMet: boolean;

    if (connector === 'OR') {
      dependenciesNotMet = falsyValues?.some((field) => !object[field]) ?? true;
      dependenciesMet = truthlyValues?.some((field) => !!object[field]) ?? true;
    } else {
      dependenciesNotMet =
        falsyValues?.every((field) => !object[field]) ?? true;
      dependenciesMet =
        truthlyValues?.every((field) => !!object[field]) ?? true;
    }

    const areDependenciesMet = dependenciesNotMet && dependenciesMet;

    if (!isValueInCondition) {
      return (
        areDependenciesMet &&
        (deniedObjectFieldValue
          ? !objectFieldValue
          : (objectFieldValue ?? false))
      );
    } else return areDependenciesMet && isValidValue;
  }

  if (!isValueInCondition) {
    return deniedObjectFieldValue ? !objectFieldValue : !!objectFieldValue;
  }

  return isValidValue;
};

export const checkConditionsInObject = <T>(
  object: T,
  conditions: Array<CheckConditionsType<T>>,
  conditionConnector?: 'AND' | 'OR'
) => {
  if (conditionConnector === 'OR') {
    return conditions.some((condition) => checkCondition(object, condition));
  }

  return conditions.every((condition) => checkCondition(object, condition));
};

export const downloadDocument = (
  base64: string,
  documentType?: string,
  filename: string = 'file'
) => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], {
    type: documentType,
  });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');

  link.href = url;
  link.download = filename ?? 'file';
  link.click();

  URL.revokeObjectURL(url);
};

/**
 * Saves a cross-origin file (e.g. a public S3 object). `<a download>` is ignored
 * cross-origin, so the file is fetched as a blob first. `no-store` avoids reusing
 * a response cached without `Origin` (e.g. by a `<video>`): S3 sends it without
 * CORS headers and without `Vary: Origin`, so the fetch would fail.
 */
export const downloadRemoteFile = async (url: string, filename: string) => {
  const response = await fetch(url, { cache: 'no-store' });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);

  const href = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Revoking right away can abort the download (notably in Firefox).
  setTimeout(() => URL.revokeObjectURL(href), 10_000);
};

export const incrementFailures = (
  failureName: keyof Omit<
    GlobalFailureStoreType,
    '_listeners' | 'subscribe' | 'notify' | 'unsubscribe'
  >
) => {
  GlobalFailureStore[failureName]++;

  // Notifies all subscribers when a values changes
  GlobalFailureStore.notify();
};

export const resetFailures = (
  failureName: keyof Omit<
    GlobalFailureStoreType,
    '_listeners' | 'subscribe' | 'notify' | 'unsubscribe'
  >
) => {
  GlobalFailureStore[failureName] = 0;

  // Notifies all subscribers when a values changes
  GlobalFailureStore.notify();
};

export const orderStatusToKanbanColumn = (status: OrderStatusChoicesType) => {
  let result;

  switch (status) {
    case 'identifying_car':
    case 'identifying_products':
    case 'searching_oems':
    case 'searching_stock':
    case 'order_quoted':
      result = ['to_quote', true] as const;
      break;
    case 'quotation_sent':
      result = ['quotation_sent', false] as const;
      break;
    case 'options_accepted':
      result = ['options_accepted', false] as const;
      break;
    case 'purchase_completed':
      result = ['sale_orders', true] as const;
      break;
    case 'finished':
      result = ['finished', false] as const;
      break;
    case 'canceled':
      result = ['canceled', false] as const;
      break;
    case 'devolution_requested':
      result = ['devolution_requested', false] as const;
      break;
    default:
      result = [status, false] as const;
      break;
  }

  return result;
};

export const parseHoursTo24Hours = (hour: string) => {
  const trimmedHour = hour.trim().toLowerCase();
  const [hourMin, ampm] = trimmedHour.split(' ');

  const formatedPmAm = ampm.replace(/[ .\s]/g, '');

  let [h, m] = hourMin.split(':').map(Number);

  if (formatedPmAm === 'pm' && h !== 12) h += 12;
  if (formatedPmAm === 'am' && h === 12) h = 0;

  const date = new Date();

  date.setHours(h, m, 0, 0);

  return date;
};
